/**
 * Recovery Loop status band — the drawer's single loud element (approved
 * prototype v3, 2026-07-12: https://vitalrev-sms-recovery.vercel.app).
 *
 * The band replaces what used to be said four ways at once — the drawer stepper,
 * a separate banner, and the header status pill. Each fact now appears once, and
 * volume tracks urgency. Its anatomy:
 *   - a bold phrase saying exactly where the case stands,
 *   - one short line saying why and what happens next,
 *   - the journey dots underneath (rendered by the component, not here).
 *
 * The hue says WHOSE MOVE IT IS, which is the whole point of the color:
 *   amber = the patient's · blue/purple = our automation's · red = staff's ·
 *   green = recovered · gray = nobody's (closed, opted out, nothing to do).
 *
 * Pure and clock-injected so every state's copy is unit-tested without
 * rendering — the words are the deliverable here, and they're what staff read
 * at 8 AM to decide whether a case needs them.
 */
import type { RecoveryDisplayState } from '../services/recoveryCases'
import { MAX_NUDGES, NUDGE_HALTED, NUDGE_INTERVAL_HOURS } from '../../shared/smsNudge'
import { formatNudgeSendTime, normalizeQuietHours, type QuietHours } from './nudgeTime'

/** Band colours — one per "whose move is it", matching the prototype's classes. */
export type BandHue = 'amber' | 'blue' | 'purple' | 'green' | 'red' | 'gray'

export interface RecoveryBand {
  hue: BandHue
  /** The bold phrase. Never ends in a period — it's a label, not a sentence. */
  headline: string
  /** One line of why + what happens next. May be empty when there's nothing
   *  honest to add (better silent than padded with filler). */
  subline: string
}

/**
 * Everything the copy can key on. All the optional fields are things we may or
 * may not have for a given case — the copy degrades to a shorter true sentence
 * rather than inventing a value (a wrong "nudge at 11:30" is worse than no time
 * at all, because staff will plan around it).
 */
export interface RecoveryBandInput {
  state: RecoveryDisplayState
  /** A staff member closed the case by hand — NOT a loop recovery. */
  handled: boolean
  /** ISO instant of that close, for "handled outside VitalRev on Jul 3". */
  handledAt?: string | null
  /** ISO instant of the last text we sent — drives "Texted 4h ago". */
  lastOutboundAt?: string | null
  /** ISO due time of the next nudge; NUDGE_HALTED = the cadence stopped. */
  nextNudgeAt?: string | null
  /** Nudges already sent this episode (0 right after the first outreach). */
  nudgeCount?: number | null
  /** Carrier error code from the failed outbound (Telnyx) — e.g. "30005". */
  errorCode?: string | null
  /** Why a rerun_failed case parked (#269) — "rounds_exhausted"/"repeat_fields"
   *  mean the loop made real progress before stopping, and the band says so. */
  rerunFailureReason?: string | null
  /** ISO instant a card answering a SIBLING case landed on this thread (#312).
   *  The default clarification copy ("the card was readable but one field is
   *  missing") is false here — no card was ever read for THIS case — and the
   *  old `sms_sent` copy ("no card yet") was false too, with the photo visible
   *  in the same thread. Set → the band says what actually happened. */
  siblingCardArrivedAt?: string | null
  /** ISO instant a photo landed on this thread that we could not match to any
   *  case (#312). The case is still legitimately waiting for a card — this one
   *  may not be theirs — but "no card yet" is false while it sits in the same
   *  thread, so the band says what is actually happening. */
  unattributedCardAt?: string | null
  /** ISO instant a card landed on this thread that the ladder bound to ANOTHER
   *  patient's case (#312 review). We deliberately send no text — the person
   *  sending knows there is a second card and is usually mid-send — but the
   *  photo is visibly in this thread, so "no card yet" is false regardless of
   *  whether we chose to ask. */
  otherPatientCardAt?: string | null
  /** The CLINIC's IANA zone — quiet hours are the clinic's, not the reader's.
   *  Absent (legacy clinic doc) → the copy drops the time instead of guessing. */
  timeZone?: string | null
  /** The clinic's outbound window; absent = the global default. */
  quietHours?: QuietHours | null
  /** Injected clock so the relative phrases are deterministic in tests. */
  now?: Date
}

/** "4h ago" / "22m ago" / "yesterday" — the same vocabulary as the case rows. */
function ago(iso: string | null | undefined, now: Date): string | null {
  if (!iso) return null
  const then = Date.parse(iso)
  if (!Number.isFinite(then)) return null
  const mins = Math.floor((now.getTime() - then) / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return days === 1 ? 'yesterday' : `${days}d ago`
}

/**
 * When the nudge will actually go out, in the clinic's own zone and window.
 * NOT the raw schedule: a due time inside quiet hours is deferred by the sweep,
 * so printing it verbatim would promise a send that can't happen. Without a
 * timezone we say nothing about time at all — see utils/nudgeTime.
 */
function sendTime(input: RecoveryBandInput, now: Date): string | null {
  if (!input.nextNudgeAt || !input.timeZone) return null
  return formatNudgeSendTime(
    input.nextNudgeAt,
    input.timeZone,
    normalizeQuietHours(input.quietHours),
    now,
  )
}

/**
 * The "what the automation does next" line for a case still waiting on the
 * patient. Three honest shapes, in descending order of what we know:
 *   - a scheduled nudge  → name it, with its number and time
 *   - the cadence halted → say the chasing has stopped, so staff know it's
 *                          theirs now (the appointment passed, or we aged out)
 *   - nothing known      → just how long it's been quiet
 */
function nudgeLine(input: RecoveryBandInput, now: Date): string {
  const texted = ago(input.lastOutboundAt, now)
  const sent = input.nudgeCount ?? 0
  const next = input.nextNudgeAt

  if (next && next !== NUDGE_HALTED && sent < MAX_NUDGES) {
    const at = sendTime(input, now)
    const which = `Nudge ${sent + 1} of ${MAX_NUDGES}`
    return at
      ? `${which} at ${at} · quiet hours respected · nothing needed from you.`
      : `${which} scheduled · quiet hours respected · nothing needed from you.`
  }
  if (next === NUDGE_HALTED || sent >= MAX_NUDGES) {
    return texted
      ? `Texted ${texted} · automated nudges are done — it's yours now.`
      : "Automated nudges are done — it's yours now."
  }
  return texted ? `Texted ${texted}, no reply yet.` : ''
}

/** The full nudge explanation for the tab bodies — the cadence spelled out. */
export function nudgeExplainer(input: RecoveryBandInput, now = new Date()): string {
  const texted = ago(input.lastOutboundAt, now)
  const opener = texted ? `Texted ${texted}. ` : ''
  const next = input.nextNudgeAt
  const sent = input.nudgeCount ?? 0
  if (!next || next === NUDGE_HALTED || sent >= MAX_NUDGES) {
    return `${opener}The automated nudges are finished — nothing more goes out on its own. Message the patient here or reach them your usual way.`
  }
  const at = sendTime(input, now)
  return `${opener}The agent sends up to ${MAX_NUDGES} nudges — ${at ? `${at}, ` : ''}then every ${NUDGE_INTERVAL_HOURS}h until the appointment — each worded differently, never during quiet hours. If the patient never replies, it hands off to staff before the visit.`
}

/**
 * The in-thread note under a text that didn't land. Wording is the PO's own,
 * verbatim (2026-07-13) — staff were guessing why sends failed, and "every dead
 * end needs to say what happened and what to do next, in your face".
 *
 * The two failures are genuinely different situations: a carrier REJECTION means
 * the number never was dialable, while an accepted-then-undeliverable send means
 * it looked fine and died downstream. Both end at "verify the number", but only
 * one of them tells staff the record was wrong from the start.
 */
export function deliveryFailureNote(
  message: { status: string; errorCode?: string | null },
): string | null {
  const code = message.errorCode ? ` (carrier code ${message.errorCode})` : ''
  switch (message.status) {
    case 'sending_failed':
      return `The carrier rejected this number${code} — it may be invalid. Verify the phone number, then message them again.`
    case 'delivery_failed':
      return `A text was sent, but it never reached the patient${code} — the number may be wrong, disconnected, or a landline. Verify the phone number, then message them again.`
    default:
      return null
  }
}

/**
 * The band for one case. `handled` is checked FIRST and deliberately: a case a
 * human gave up on and a case the loop recovered are different claims, and
 * "the loop closed 12 cases this week" is a number we put in front of
 * customers — it cannot be padded with dead ends (PO, 2026-07-13).
 */
export function recoveryBandFor(input: RecoveryBandInput, now = new Date()): RecoveryBand {
  const { state, handled } = input

  if (handled && state !== 'resolved') {
    const on = input.handledAt ? new Date(input.handledAt) : null
    const day = on && Number.isFinite(on.getTime())
      ? ` on ${on.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
      : ''
    return {
      hue: 'gray',
      headline: 'Closed by staff — not a loop recovery',
      subline: `The loop didn't finish this one; a staff member handled it outside VitalRev${day}.`,
    }
  }

  switch (state) {
    case 'sms_sent':
      // #312: a photo IS on the thread, we just don't know yet whose it is.
      // Claiming nothing arrived sends staff chasing a patient who replied.
      if (input.unattributedCardAt) {
        return {
          hue: 'blue',
          headline: 'A card came in — checking who it belongs to',
          subline:
            'We asked the patient which patient this card is for. ' +
            'Nothing applied yet.',
        }
      }
      // #312: a card DID arrive on this thread — it answered another patient's
      // check. We stayed quiet on purpose (whoever is sending knows there is a
      // second card), but this case is still waiting, and staff need to know
      // the difference between silence and a reply that wasn't theirs.
      if (input.otherPatientCardAt) {
        return {
          hue: 'blue',
          headline: 'A card came in — for another patient on this thread',
          subline:
            "The photo answered another patient's check. " +
            'This one still needs its own card.',
        }
      }
      return {
        hue: 'amber',
        headline: 'Waiting on the patient — no card yet',
        subline: nudgeLine(input, now),
      }

    case 'awaiting_reply':
      return {
        hue: 'amber',
        headline: 'Waiting on the patient — we asked for one more detail',
        subline: nudgeLine(input, now),
      }

    case 'escalated':
      // #304. The band's hue says whose move it is, and this one is a person's.
      // It must also say the automation has stopped: staff who assume the loop
      // is still working it are exactly how the reported thread went unanswered
      // until the next morning.
      return {
        hue: 'red',
        headline: 'Needs a person — the patient asked for one',
        subline:
          'Automated messages have stopped on this thread. ' +
          'Call or message them here; nothing else goes out on its own.',
      }

    case 'needs_clarification':
      // #312: the patient DID send a card — it just answered a sibling case on
      // the same thread. Saying "the card was readable but a field is missing"
      // would describe a read that never happened for this case.
      if (input.siblingCardArrivedAt) {
        return {
          hue: 'blue',
          headline: 'Asking the patient for their other card',
          subline:
            'They sent a card for a different plan. ' +
            'We asked for the one this check needs.',
        }
      }
      return {
        hue: 'blue',
        headline: 'Asking the patient for the missing detail',
        subline: 'The card was readable but one needed field is still missing. Nothing applied yet.',
      }

    case 'extracted':
      return {
        hue: 'purple',
        headline: 'Re-running the check',
        subline: 'Card details captured — a fresh eligibility check is on its way.',
      }

    case 'check_rerun':
      return {
        hue: 'purple',
        headline: 'Re-running the check',
        subline: 'Corrected details submitted — a new result is expected within a couple of minutes.',
      }

    case 'resolved':
      return {
        hue: 'green',
        headline: 'Recovered — coverage verified',
        subline: 'The corrected card details came back active on the re-run.',
      }

    case 'extracted_same':
      return {
        hue: 'gray',
        headline: 'No change — the card matches what we already had',
        subline: "Nothing was worth re-running. The original failure needs a different fix.",
      }

    case 'rerun_failed':
      // #269: "gave up after real progress" and "never got anything usable"
      // are different situations for staff — the reason says which this is.
      if (input.rerunFailureReason === 'rounds_exhausted') {
        return {
          hue: 'red',
          headline: 'Needs review — real progress, one question left',
          subline:
            "The patient's correction worked and the payer flagged a NEW field, but the ask limit was reached. One staff question likely closes this.",
        }
      }
      if (input.rerunFailureReason === 'repeat_fields') {
        return {
          hue: 'red',
          headline: 'Needs review — the same field failed again',
          subline:
            "The patient's answer didn't fix the flagged field. Verify it with the payer or call the patient.",
        }
      }
      return {
        hue: 'red',
        headline: 'Needs review — the re-run failed too',
        subline: "The corrected details still didn't verify. Call the patient or check the payer.",
      }

    case 'cannot_extract':
      return {
        hue: 'red',
        headline: "Needs review — the card couldn't be read",
        subline: 'Nothing was applied. Request a new photo or call the patient.',
      }

    case 'sms_failed':
      return {
        hue: 'red',
        headline: 'The text never reached the patient',
        subline: input.errorCode
          ? `Carrier code ${input.errorCode} — the number may be wrong, disconnected, or a landline. Verify the phone number.`
          : 'The number may be wrong, disconnected, or a landline. Verify the phone number.',
      }

    case 'no_phone':
      return {
        hue: 'red',
        headline: 'Never started — no phone number on file',
        subline: 'Add a phone number and the outreach starts automatically.',
      }

    case 'opted_out':
      return {
        hue: 'gray',
        headline: 'Patient opted out of texts',
        subline: 'They replied STOP. Call the patient or resolve it at their visit.',
      }
  }
}
